/**
 * TeamPage — the supervisor Patient-Advocate Team view (wireframe 4, ADH-007).
 *
 * The hero of Phase 4. Composes:
 *   - a totals KPI strip (team size, active load, due today, SLA risk, conversion)
 *   - the team table (member, active, due, SLA risk, conversion, utilisation,
 *     last active) with a per-row Reassign action (POST /workforce/assign)
 *   - structured escalation cards (reason + recommended action) in the right
 *     context panel
 *   - an AIInsightCard load-balancing advisory ("reassign N cases from X to Y"),
 *     advisory-only with a one-click Accept that runs the reassignment (FND-010)
 *   - a resourcing / assignment-rules sub-view (GET /workforce/assignment-rules)
 *
 * Data via useWorkforce(): GET /workforce/team, /escalations, /assignment-rules.
 * Each surface degrades gracefully to a deterministic seed on 404 so the design
 * always renders; a small notice flags any surface still served from the seed.
 */
import { useMemo, useState, type CSSProperties } from 'react';
import {
  useWorkforce,
  type AssignmentRule,
  type Escalation,
  type TeamMember,
} from '../hooks/useWorkforce';
import { colors, radius, space } from '../theme';
import { PageHeader } from '../components/PageHeader';
import { RoleImpactStrip } from '../components/PlatformImpactStrip';
import { ThreePanel } from '../components/ThreePanel';
import { Card } from '../components/Card';
import { DataTable, type Column } from '../components/DataTable';
import { StatusBadge } from '../components/StatusBadge';
import { PriorityPill } from '../components/PriorityPill';
import { RoleBadge } from '../components/RoleBadge';
import { AIInsightCard } from '../components/AIInsightCard';
import { LoadingIndicator } from '../components/LoadingIndicator';
import { EmptyState } from '../components/EmptyState';
import { Toolbar } from '../components/Toolbar';
import { useShellSlot } from '../components/shellContext';

function fmtRelative(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  const mins = Math.round((Date.now() - d.getTime()) / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

function pct(n: number): string {
  return `${Math.round(n * 100)}%`;
}

function utilTier(util: number | null | undefined): 'critical' | 'high' | 'normal' | 'low' {
  const u = util ?? 0;
  if (u >= 1.0) return 'critical';
  if (u >= 0.85) return 'high';
  if (u >= 0.5) return 'normal';
  return 'low';
}

const ESCALATION_REASON: Record<string, string> = {
  sla_breach: 'SLA breach',
  overloaded: 'Advocate overloaded',
  stuck_stage: 'Case stalled',
  ghosting: 'Patient unresponsive',
};

function reasonLabel(reason: string): string {
  return ESCALATION_REASON[reason] ?? reason.replace(/_/g, ' ');
}

function severityToSemantic(severity: string): 'danger' | 'warning' | 'neutral' {
  const s = severity.toLowerCase();
  if (s === 'high') return 'danger';
  if (s === 'med' || s === 'medium') return 'warning';
  return 'neutral';
}

type Tab = 'team' | 'rules';

export function TeamPage() {
  useShellSlot({ breadcrumb: 'Patient-Advocate Team', stage: 'Supervisor' });
  const {
    team,
    escalations,
    rules,
    totals,
    suggestion,
    teamAvailable,
    escalationsAvailable,
    rulesAvailable,
    isLoading,
    isWorking,
    error,
    actionError,
    refresh,
    reassign,
  } = useWorkforce();

  const [tab, setTab] = useState<Tab>('team');
  const [reassignFor, setReassignFor] = useState<TeamMember | null>(null);

  const anySeeded = !teamAvailable || !escalationsAvailable || !rulesAvailable;

  const acceptSuggestion = async () => {
    if (!suggestion) return;
    await reassign({
      from_user_id: suggestion.from_user_id,
      to_user_id: suggestion.to_user_id,
      count: suggestion.count,
    });
  };

  const teamColumns: Column<TeamMember>[] = useMemo(
    () => [
      {
        key: 'member',
        header: 'Advocate',
        render: (m) => (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontWeight: 600 }}>{m.name}</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <RoleBadge role={m.role} />
              {m.status && (
                <span style={{ fontSize: '0.72rem', color: colors.textSecondary }}>
                  ● {m.status}
                </span>
              )}
            </div>
          </div>
        ),
      },
      { key: 'active', header: 'Active', align: 'right', render: (m) => <strong>{m.active_cases}</strong> },
      { key: 'due', header: 'Due today', align: 'right', render: (m) => m.due_today },
      {
        key: 'sla',
        header: 'SLA risk',
        align: 'right',
        render: (m) =>
          m.sla_risk > 0 ? (
            <span style={{ color: colors.danger, fontWeight: 700 }}>{m.sla_risk}</span>
          ) : (
            <span style={{ color: colors.textMuted }}>0</span>
          ),
      },
      {
        key: 'conversion',
        header: 'Conversion',
        align: 'right',
        render: (m) => pct(m.conversion_rate),
      },
      {
        key: 'util',
        header: 'Load',
        render: (m) => <PriorityPill tier={utilTier(m.utilization)} style={{ textTransform: 'none' }} />,
      },
      { key: 'last', header: 'Last active', render: (m) => fmtRelative(m.last_active) },
      {
        key: 'action',
        header: '',
        align: 'right',
        render: (m) => (
          <button type="button" style={styles.smallBtn} onClick={() => setReassignFor(m)}>
            Reassign
          </button>
        ),
      },
    ],
    []
  );

  const ruleColumns: Column<AssignmentRule>[] = useMemo(
    () => [
      { key: 'priority', header: '#', align: 'right', width: 48, render: (r) => r.priority ?? '—' },
      {
        key: 'name',
        header: 'Rule',
        render: (r) => (
          <div>
            <div style={{ fontWeight: 600 }}>{r.name}</div>
            <div style={styles.muted}>{r.description}</div>
          </div>
        ),
      },
      { key: 'dim', header: 'Routes on', render: (r) => <StatusBadge value={r.dimension} status="info" /> },
      { key: 'target', header: 'Target', render: (r) => r.target ?? '—' },
      {
        key: 'enabled',
        header: 'Status',
        render: (r) => <StatusBadge value={r.enabled ? 'active' : 'disabled'} />,
      },
    ],
    []
  );

  return (
    <>
      <PageHeader
        greetingName="Supervisor"
        greetingTail="Here's how your team is tracking."
        title="Patient-Advocate Team"
        subtitle="Capacity, SLA risk, and conversion across the advocate team (ADH-007). Rebalance work and review structured escalations; AI load-balancing is advisory and confirmed by you."
        actions={
          <button type="button" className="btn btn-secondary btn-sm" onClick={refresh} disabled={isLoading}>
            Refresh
          </button>
        }
      />

      <RoleImpactStrip />

      {anySeeded && (
        <p style={styles.seedNotice} role="status">
          Showing seeded team data for surfaces not yet wired (
          {[
            !teamAvailable && 'team',
            !escalationsAvailable && 'escalations',
            !rulesAvailable && 'rules',
          ]
            .filter(Boolean)
            .join(', ')}
          ). Live data appears automatically when the workforce service is online.
        </p>
      )}

      {error && (
        <p style={styles.error} role="alert">
          {error.message}
        </p>
      )}

      {/* Totals KPI strip */}
      <div style={styles.kpiStrip}>
        <Kpi label="Team members" value={String(totals.members)} />
        <Kpi label="Active cases" value={String(totals.active_cases)} />
        <Kpi label="Due today" value={String(totals.due_today)} accent={colors.warning} />
        <Kpi
          label="At SLA risk"
          value={String(totals.sla_risk)}
          accent={totals.sla_risk > 0 ? colors.danger : colors.success}
        />
        <Kpi label="Avg conversion" value={pct(totals.avg_conversion)} accent={colors.action} />
      </div>

      {/* Tabs */}
      <Toolbar style={{ marginBottom: 16 }}>
        <div style={styles.tabs}>
          <TabBtn active={tab === 'team'} onClick={() => setTab('team')}>
            Team
          </TabBtn>
          <TabBtn active={tab === 'rules'} onClick={() => setTab('rules')}>
            Assignment rules
          </TabBtn>
        </div>
      </Toolbar>

      {tab === 'team' ? (
        <ThreePanel
          primary={
            <>
              <Card title="Team capacity & performance" padded={false}>
                {isLoading ? (
                  <div style={{ padding: 24 }}>
                    <LoadingIndicator text="Loading team…" />
                  </div>
                ) : (
                  <DataTable
                    columns={teamColumns}
                    rows={team}
                    rowKey={(m) => m.user_id}
                    emptyTitle="No advocates on this team"
                  />
                )}
              </Card>

              {suggestion && (
                <AIInsightCard
                  heading="AI load-balancing suggestion"
                  confidence={suggestion.confidence}
                  acceptLabel={`Reassign ${suggestion.count} cases`}
                  onAccept={acceptSuggestion}
                >
                  Reassign <strong>{suggestion.count}</strong> case
                  {suggestion.count === 1 ? '' : 's'} from{' '}
                  <strong>{suggestion.from_name}</strong> to{' '}
                  <strong>{suggestion.to_name}</strong>. {suggestion.rationale}
                </AIInsightCard>
              )}
            </>
          }
          rightContext={
            <>
              <h3 style={styles.contextTitle}>
                Escalations{' '}
                <span style={styles.countChip}>{escalations.length}</span>
              </h3>
              {escalations.length === 0 ? (
                <EmptyState title="No open escalations" description="The team is on track." />
              ) : (
                escalations.map((e) => <EscalationCard key={e.id} esc={e} />)
              )}
            </>
          }
        />
      ) : (
        <Card
          title="Resourcing & auto-assignment rules"
          padded={false}
        >
          <div style={{ padding: '0 0 4px' }}>
            <p style={{ ...styles.muted, padding: '12px 18px 0' }}>
              Rules evaluate top-to-bottom; the first match assigns the case (ADH-007). Load
              balancing is the final fallback within an eligible pool.
            </p>
            <DataTable
              columns={ruleColumns}
              rows={rules}
              rowKey={(r) => r.id}
              emptyTitle="No assignment rules configured"
            />
          </div>
        </Card>
      )}

      {reassignFor && (
        <ReassignDialog
          member={reassignFor}
          team={team}
          isWorking={isWorking}
          error={actionError?.message ?? null}
          onClose={() => setReassignFor(null)}
          onSubmit={async (toId, count) => {
            const ok = await reassign({
              from_user_id: reassignFor.user_id,
              to_user_id: toId,
              count,
            });
            if (ok) setReassignFor(null);
          }}
        />
      )}
    </>
  );
}

function Kpi({ label, value, accent }: { label: string; value: string; accent?: string }) {
  return (
    <div style={styles.kpiCard}>
      <div style={{ ...styles.kpiValue, color: accent ?? colors.textPrimary }}>{value}</div>
      <div style={styles.kpiLabel}>{label}</div>
    </div>
  );
}

function TabBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        ...styles.tabBtn,
        ...(active ? styles.tabBtnActive : null),
      }}
    >
      {children}
    </button>
  );
}

function EscalationCard({ esc }: { esc: Escalation }) {
  const sem = severityToSemantic(esc.severity);
  return (
    <div style={styles.escCard}>
      <div style={styles.escHead}>
        <StatusBadge value={reasonLabel(esc.reason)} status={sem} />
        {esc.case_id && <span style={styles.escCase}>{esc.case_id}</span>}
      </div>
      {esc.patient && <div style={styles.escPatient}>{esc.patient}</div>}
      {esc.detail && <div style={styles.escDetail}>{esc.detail}</div>}
      <div style={styles.escMeta}>
        {esc.owner && <span>Owner: {esc.owner}</span>}
        {esc.detected_at && <span>· {fmtRelative(esc.detected_at)}</span>}
      </div>
      {esc.recommended_action && (
        <div style={styles.escAction}>↪ {esc.recommended_action}</div>
      )}
    </div>
  );
}

function ReassignDialog({
  member,
  team,
  isWorking,
  error,
  onClose,
  onSubmit,
}: {
  member: TeamMember;
  team: TeamMember[];
  isWorking: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (toUserId: string, count: number) => void;
}) {
  const others = team.filter((m) => m.user_id !== member.user_id);
  const [toId, setToId] = useState<string>(others[0]?.user_id ?? '');
  const [count, setCount] = useState<number>(Math.min(3, member.active_cases));

  return (
    <div style={styles.overlay} role="dialog" aria-modal="true" aria-label="Reassign cases">
      <div style={styles.dialog}>
        <h3 style={styles.dialogTitle}>Reassign cases from {member.name}</h3>
        <p style={styles.muted}>
          {member.name} has {member.active_cases} active cases ({member.sla_risk} at SLA risk).
        </p>
        <label style={styles.field}>
          <span style={styles.label}>Move to</span>
          <select value={toId} onChange={(e) => setToId(e.target.value)} style={styles.input}>
            {others.map((m) => (
              <option key={m.user_id} value={m.user_id}>
                {m.name} · {m.active_cases} active
              </option>
            ))}
          </select>
        </label>
        <label style={styles.field}>
          <span style={styles.label}>Number of cases</span>
          <input
            type="number"
            min={1}
            max={member.active_cases}
            value={count}
            onChange={(e) => setCount(Math.max(1, Math.min(member.active_cases, Number(e.target.value) || 1)))}
            style={styles.input}
          />
        </label>
        {error && (
          <p style={styles.error} role="alert">
            {error}
          </p>
        )}
        <div style={styles.dialogActions}>
          <button type="button" style={styles.smallBtn} onClick={onClose} disabled={isWorking}>
            Cancel
          </button>
          <button
            type="button"
            style={styles.primaryBtn}
            disabled={isWorking || !toId}
            onClick={() => onSubmit(toId, count)}
          >
            {isWorking ? 'Reassigning…' : 'Reassign'}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  seedNotice: {
    margin: '0 0 16px',
    padding: '8px 12px',
    background: colors.brandTint,
    color: colors.brandActive,
    borderRadius: radius.sm,
    fontSize: '0.82rem',
  },
  error: { color: colors.danger, fontSize: '0.9rem', margin: '0 0 12px' },
  kpiStrip: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: 12,
    marginBottom: space.lg,
  },
  kpiCard: {
    background: colors.white,
    border: `1px solid ${colors.border}`,
    borderRadius: radius.md,
    padding: '14px 16px',
  },
  kpiValue: { fontSize: '1.6rem', fontWeight: 700, lineHeight: 1.1 },
  kpiLabel: { fontSize: '0.78rem', color: colors.textSecondary, marginTop: 4 },
  tabs: { display: 'flex', gap: 4 },
  tabBtn: {
    padding: '7px 14px',
    background: 'transparent',
    border: `1px solid ${colors.border}`,
    borderRadius: radius.pill,
    fontSize: '0.85rem',
    fontWeight: 600,
    color: colors.textSecondary,
    cursor: 'pointer',
  },
  tabBtnActive: { background: colors.brandTint, color: colors.brandActive, borderColor: colors.brandTint },
  contextTitle: {
    margin: 0,
    fontSize: '0.95rem',
    fontWeight: 700,
    color: colors.textPrimary,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  countChip: {
    background: colors.surfaceAlt,
    color: colors.textSecondary,
    borderRadius: radius.pill,
    padding: '1px 8px',
    fontSize: '0.74rem',
    fontWeight: 700,
  },
  escCard: {
    background: colors.white,
    border: `1px solid ${colors.border}`,
    borderRadius: radius.md,
    padding: 12,
    display: 'flex',
    flexDirection: 'column',
    gap: 5,
  },
  escHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 },
  escCase: { fontSize: '0.78rem', fontWeight: 600, color: colors.textSecondary },
  escPatient: { fontWeight: 600, fontSize: '0.9rem' },
  escDetail: { fontSize: '0.84rem', color: colors.textPrimary, lineHeight: 1.4 },
  escMeta: { fontSize: '0.74rem', color: colors.textSecondary, display: 'flex', gap: 6, flexWrap: 'wrap' },
  escAction: {
    fontSize: '0.8rem',
    color: colors.action,
    fontWeight: 600,
    marginTop: 2,
    lineHeight: 1.35,
  },
  muted: { color: colors.textSecondary, fontSize: '0.82rem' },
  smallBtn: {
    padding: '5px 12px',
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: radius.sm,
    fontSize: '0.82rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  primaryBtn: {
    padding: '7px 16px',
    background: colors.action,
    color: colors.white,
    border: 'none',
    borderRadius: radius.sm,
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(28,25,23,0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 50,
  },
  dialog: {
    background: colors.white,
    borderRadius: radius.lg,
    padding: 24,
    width: 'min(420px, 92vw)',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  dialogTitle: { margin: 0, fontSize: '1.1rem', fontWeight: 700 },
  dialogActions: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 },
  field: { display: 'flex', flexDirection: 'column', gap: 4 },
  label: { fontSize: '0.82rem', fontWeight: 600, color: colors.textSecondary },
  input: {
    padding: '8px 10px',
    border: `1px solid ${colors.border}`,
    borderRadius: radius.sm,
    fontSize: '0.92rem',
  },
};
